import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow } from "../utils/apiRequestError";
import type { DashboardLayout } from "../types/dashboardLayout";
import type { DashboardLayoutAccountScope } from "../types/dashboardLayoutAccountScope";

export interface DashboardLayoutsListResponse {
  activeLayoutId: string;
  layouts: DashboardLayout[];
}

export interface CreateDashboardLayoutRequest {
  name: string;
  layoutVersion: number;
  items: DashboardLayout["items"];
}

export interface UpdateDashboardLayoutRequest {
  name?: string;
  isActive?: boolean;
  layoutVersion?: number;
  items?: DashboardLayout["items"];
}

function accountQuery(scope: DashboardLayoutAccountScope): string {
  const params = new URLSearchParams({
    broker_name: scope.brokerName.trim(),
    broker_account_code: scope.brokerAccountCode.trim(),
  });
  return params.toString();
}

function scopedPath(path: string, scope: DashboardLayoutAccountScope): string {
  const query = accountQuery(scope);
  return `${path}?${query}`;
}

async function parseLayoutJsonOrThrow<T>(response: Response, fallback: string): Promise<T> {
  const data = await parseJsonOrThrow<{ data?: T } | T>(response, fallback);
  return (data && typeof data === "object" && "data" in data ? data.data : data) as T;
}

export async function listDashboardLayouts(
  scope: DashboardLayoutAccountScope
): Promise<DashboardLayoutsListResponse> {
  const response = await authenticatedFetch(scopedPath("/dashboard/layouts", scope));
  return parseLayoutJsonOrThrow(response, `Failed to load dashboard layouts: ${response.status}`);
}

export async function createDashboardLayout(
  scope: DashboardLayoutAccountScope,
  body: CreateDashboardLayoutRequest
): Promise<DashboardLayout> {
  const response = await authenticatedFetch(scopedPath("/dashboard/layouts", scope), {
    method: "POST",
    body: JSON.stringify(body),
  });
  return parseLayoutJsonOrThrow(response, `Failed to create dashboard layout: ${response.status}`);
}

export async function updateDashboardLayout(
  scope: DashboardLayoutAccountScope,
  layoutId: string,
  patch: UpdateDashboardLayoutRequest
): Promise<DashboardLayout> {
  const response = await authenticatedFetch(
    scopedPath(`/dashboard/layouts/${encodeURIComponent(layoutId)}`, scope),
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    }
  );
  return parseLayoutJsonOrThrow(response, `Failed to update dashboard layout: ${response.status}`);
}

export async function deleteDashboardLayout(
  scope: DashboardLayoutAccountScope,
  layoutId: string
): Promise<void> {
  const response = await authenticatedFetch(
    scopedPath(`/dashboard/layouts/${encodeURIComponent(layoutId)}`, scope),
    {
      method: "DELETE",
    }
  );
  await parseJsonOrThrow(response, `Failed to delete dashboard layout: ${response.status}`, {
    allow204: true,
  });
}

export async function activateDashboardLayout(
  scope: DashboardLayoutAccountScope,
  layoutId: string
): Promise<DashboardLayoutsListResponse> {
  const response = await authenticatedFetch(
    scopedPath(`/dashboard/layouts/${encodeURIComponent(layoutId)}/activate`, scope),
    {
      method: "POST",
    }
  );
  return parseLayoutJsonOrThrow(response, `Failed to activate dashboard layout: ${response.status}`);
}

export async function duplicateDashboardLayout(
  scope: DashboardLayoutAccountScope,
  layoutId: string
): Promise<DashboardLayout> {
  const response = await authenticatedFetch(
    scopedPath(`/dashboard/layouts/${encodeURIComponent(layoutId)}/duplicate`, scope),
    {
      method: "POST",
    }
  );
  return parseLayoutJsonOrThrow(response, `Failed to duplicate dashboard layout: ${response.status}`);
}
